import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet, Alert, Platform } from 'react-native';
import { useTheme } from '~contexts/ThemeContext';
import { spacing, typography, borderRadius } from '~constants/design';
import { STRIPE_PUBLISH_KEY } from '@env';

// Web-only imports
let PaymentElement: any = null;
let useStripe: any = null;
let useElements: any = null;

if (Platform.OS === 'web') {
  const stripeModule = require('@stripe/react-stripe-js');
  PaymentElement = stripeModule.PaymentElement;
  useStripe = stripeModule.useStripe;
  useElements = stripeModule.useElements;
}

interface StripePaymentWebProps {
  clientSecret: string;
  onSuccess?: (paymentIntentId: string) => void;
  onError?: (error: string) => void;
  onLoading?: (isLoading: boolean) => void;
  merchantDisplayName?: string;
}

// Inner component that uses Stripe hooks
const StripePaymentFormContent: React.FC<StripePaymentWebProps> = ({
  clientSecret,
  onSuccess,
  onError,
  onLoading,
  merchantDisplayName = 'Pyxi',
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { colors } = useTheme();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handlePayment = async () => {
    if (!stripe || !elements) {
      setErrorMessage('Stripe or elements not loaded');
      return;
    }

    setIsProcessing(true);
    onLoading?.(true);

    try {
      // First, submit the elements to validate and get any errors
      const { error: submitError } = await elements.submit();
      
      if (submitError) {
        setErrorMessage(submitError.message || 'Form validation failed');
        onError?.(submitError.message || 'Form validation failed');
        setIsProcessing(false);
        onLoading?.(false);
        return;
      }

      // Confirm the payment with the client secret
      const returnUrl = typeof globalThis !== 'undefined' && (globalThis as any).window 
        ? `${(globalThis as any).window.location.origin}/payment-return` 
        : 'http://localhost:3000/payment-return';
      
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed');
        onError?.(error.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        setErrorMessage(null);
        onSuccess?.(paymentIntent.id);
      }
    } catch (err: any) {
      const errorMsg = err.message || 'An error occurred';
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsProcessing(false);
      onLoading?.(false);
    }
  };

  const styles = StyleSheet.create({
    container: {
      backgroundColor: colors.white,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginVertical: spacing.md,
    },
    title: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      marginBottom: spacing.md,
    },
    cardContainer: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      marginBottom: spacing.md,
      backgroundColor: colors.surface,
    },
    errorContainer: {
      backgroundColor: colors.error + '15',
      borderRadius: borderRadius.md,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderLeftWidth: 4,
      borderLeftColor: colors.error,
    },
    errorText: {
      color: colors.error,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    button: {
      backgroundColor: colors.primary,
      borderRadius: borderRadius.lg,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
    },
    buttonText: {
      color: colors.white,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
    },
    disabledButton: {
      opacity: 0.6,
    },
  });

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Payment Information</Text>

      <View style={styles.cardContainer}>
        {/* PaymentElement handles all payment methods */}
        <PaymentElement options={{ layout: 'tabs' }} />
      </View>

      {errorMessage && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{errorMessage}</Text>
        </View>
      )}

      <TouchableOpacity
        style={[styles.button, isProcessing && styles.disabledButton]}
        onPress={handlePayment}
        disabled={!stripe || !elements || isProcessing}
      >
        {isProcessing ? (
          <ActivityIndicator color={colors.white} size="small" />
        ) : (
          <Text style={styles.buttonText}>Complete Payment</Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

// Outer component that wraps with ElementsProvider on web
const StripePaymentWeb: React.FC<StripePaymentWebProps> = (props) => {
  // On web, we need to wrap the inner component with Elements provider
  if (Platform.OS === 'web') {
    try {
      const stripeJsModule = require('@stripe/stripe-js');
      const stripeReactModule = require('@stripe/react-stripe-js');
      const ElementsProvider = stripeReactModule.Elements;
      const loadStripe = stripeJsModule.loadStripe;
      
      // Get the publish key from env
      const publishKey = STRIPE_PUBLISH_KEY || '';
      
      if (!publishKey) {
        console.warn('⚠️ STRIPE_PUBLISH_KEY not set');
        return null;
      }

      const stripePromise = loadStripe(publishKey);

      return (
        <ElementsProvider stripe={stripePromise} options={{ clientSecret: props.clientSecret }}>
          <StripePaymentFormContent {...props} />
        </ElementsProvider>
      );
    } catch (e) {
      console.error('Failed to render Stripe form:', e);
      return null;
    }
  }

  // On native, just render the form content (without hooks)
  return null;
};

export default StripePaymentWeb;

